import { AxiosError, AxiosResponse, HttpStatusCode } from 'axios';
import { BaseResponse } from '40.quickConnect.DataAccess/axios/baseResponse';
import { ResponseStatus } from '90.quickConnect.Models/enums';
import { i18n } from '80.quickConnect.Core/utils';

/**
 * Construction du message affiché pour une réponse http en erreur dont le corps n'est pas du JSON
 * (page d'erreur HTML d'Azure, d'une passerelle, d'un proxy ou d'un pare-feu...).
 * Le HTML n'est jamais affiché tel quel : on affiche un message localisé selon le code http,
 * complété du titre de la page (balise title, à défaut h1) réduit à du texte brut et tronqué.
 * Portage de HttpErrorMessage.java (application Android).
 */

/** Nombre maximal de caractères analysés dans le corps d'erreur : le titre est en tête de page */
const MAX_BODY_LENGTH = 16 * 1024;

/** Longueur maximale du titre extrait de la page HTML */
const MAX_TITLE_LENGTH = 150;

const TITLE_PATTERN = /<title[^>]*>([\s\S]*?)<\/title>/i;
const H1_PATTERN = /<h1[^>]*>([\s\S]*?)<\/h1>/i;
const TAG_PATTERN = /<[^>]*>/g;
const WHITESPACE_PATTERN = /\s+/g;

/**
 * Content-Type de la réponse, en minuscules, undefined si absent
 */
const getContentType = (response: AxiosResponse | undefined): string | undefined => {
  const contentType = response?.headers?.['content-type'];
  if (typeof contentType !== 'string' || contentType.trim() === '') return undefined;

  return contentType.toLowerCase();
};

/**
 * Vrai si la réponse annonce un corps JSON (en-tête Content-Type)
 *
 * @param response réponse http
 */
export const isJsonResponse = (response: AxiosResponse | undefined): boolean =>
  getContentType(response)?.includes('json') ?? false;

/**
 * Message localisé correspondant au code http
 *
 * @param httpCode code http de la réponse
 */
export const getLocalizedHttpErrorMessage = (httpCode: number): string => {
  let key: string;
  switch (httpCode) {
    case HttpStatusCode.Unauthorized:
      key = 'qcapp_error_http_unauthorized';
      break;
    case HttpStatusCode.Forbidden:
      key = 'qcapp_error_http_forbidden';
      break;
    case HttpStatusCode.NotFound:
      key = 'qcapp_error_http_not_found';
      break;
    case HttpStatusCode.RequestTimeout:
    case HttpStatusCode.GatewayTimeout:
      key = 'qcapp_error_http_timeout';
      break;
    case HttpStatusCode.TooManyRequests:
      key = 'qcapp_error_http_too_many_requests';
      break;
    case HttpStatusCode.BadGateway:
    case HttpStatusCode.ServiceUnavailable:
      key = 'qcapp_error_http_server_unavailable';
      break;
    default:
      key = httpCode >= 500 ? 'qcapp_error_http_server_error' : 'qcapp_error_http_unexpected';
      break;
  }

  return i18n.t(key, { ns: 'axios' });
};

/**
 * Décode les entités HTML (&amp;, &#39;...) : le résultat est affiché en texte brut, jamais interprété.
 * DOMParser produit un document inerte (aucun script exécuté, aucune ressource chargée).
 */
const decodeHtmlEntities = (text: string): string => {
  if (typeof DOMParser === 'undefined') return text;

  try {
    return new DOMParser().parseFromString(text, 'text/html').documentElement.textContent ?? text;
  } catch {
    return text;
  }
};

/**
 * Texte brut du premier élément trouvé : balises internes supprimées, entités décodées, espaces normalisés
 */
const findText = (pattern: RegExp, html: string): string | undefined => {
  const match = pattern.exec(html);
  if (!match) return undefined;

  const text = decodeHtmlEntities(match[1].replace(TAG_PATTERN, ' '));

  return text.replace(WHITESPACE_PATTERN, ' ').trim();
};

/**
 * Extrait le titre d'une page HTML (balise title, à défaut h1) en texte brut, tronqué
 *
 * @param html début de la page HTML
 * @returns le titre, undefined si absent
 */
export const extractHtmlTitle = (html: string | undefined): string | undefined => {
  if (!html) return undefined;

  let title = findText(TITLE_PATTERN, html);
  if (!title) title = findText(H1_PATTERN, html);
  if (!title) return undefined;

  if (title.length > MAX_TITLE_LENGTH) title = `${title.substring(0, MAX_TITLE_LENGTH - 1).trim()}…`;

  return title;
};

/**
 * Lit le début du corps d'erreur s'il s'agit de HTML
 * (Content-Type html, ou absent avec un contenu commençant par une balise).
 * Gère les corps texte (responseType par défaut) et Blob (responseType 'blob' des téléchargements).
 *
 * @returns le début du HTML, undefined sinon
 */
const readHtmlBody = async (response: AxiosResponse): Promise<string | undefined> => {
  const contentType = getContentType(response);
  const declaredHtml = contentType?.includes('html') ?? false;
  // text/plain, xml... : pas de page HTML à exploiter
  if (!declaredHtml && contentType !== undefined) return undefined;

  const { data } = response;
  let content: string | undefined;
  try {
    if (typeof data === 'string') {
      content = data.substring(0, MAX_BODY_LENGTH);
    } else if (typeof Blob !== 'undefined' && data instanceof Blob) {
      content = await data.slice(0, MAX_BODY_LENGTH).text();
    }
  } catch {
    // Corps illisible : on se contente du message localisé
    return undefined;
  }

  if (content !== undefined && (declaredHtml || content.trim().startsWith('<'))) return content;

  return undefined;
};

/**
 * Message à afficher pour une réponse http en erreur dont le corps n'est pas du JSON :
 * message localisé selon le code http, complété du titre de la page HTML éventuelle.
 *
 * @param response réponse http en erreur
 */
export const buildHttpErrorMessage = async (response: AxiosResponse): Promise<string> => {
  const httpCode = response.status;
  let message = `${getLocalizedHttpErrorMessage(httpCode)} (HTTP ${httpCode})`;
  const title = extractHtmlTitle(await readHtmlBody(response));
  if (title) message += ` : ${title}`;

  return message;
};

/**
 * Remplace le corps d'une réponse http en erreur non JSON (page d'erreur HTML d'une passerelle,
 * d'un proxy, corps vide...) par une BaseResponse portant le message localisé, afin que tous les
 * appelants qui lisent response.data.message affichent un message lisible plutôt que du HTML ou "undefined".
 * Les corps JSON (réponses de l'API) et les corps binaires non HTML sont laissés intacts.
 *
 * @param error erreur axios remontée par la requête
 */
export const normalizeNonJsonErrorResponse = async (error: AxiosError): Promise<void> => {
  const { response } = error;
  if (!response || isJsonResponse(response)) return;

  const { data } = response;
  const isTextBody = data === undefined || data === null || typeof data === 'string';
  const isBlobBody = typeof Blob !== 'undefined' && data instanceof Blob;
  // ArrayBuffer, flux... : format attendu par l'appelant, on ne le modifie pas
  if (!isTextBody && !isBlobBody) return;
  // Blob non HTML (fichier téléchargé par l'appelant) : laissé intact
  if (isBlobBody && !(await readHtmlBody(response))) return;

  const normalizedData: BaseResponse = {
    status: ResponseStatus.KO,
    message: await buildHttpErrorMessage(response),
    errorCode: String(response.status),
  };
  response.data = normalizedData;
};

/**
 * Message affichable à l'utilisateur pour l'échec d'une requête http :
 * - message porté par la réponse (JSON de l'API, ou message localisé construit par normalizeNonJsonErrorResponse) ;
 * - à défaut, message localisé selon le code http ;
 * - sans réponse (coupure réseau, ou page d'erreur sans en-têtes CORS bloquée par le navigateur) : API injoignable.
 *
 * @param error erreur remontée par la requête
 * @returns le message, undefined si l'erreur n'est pas une erreur http ou si la requête a été annulée
 */
export const getDisplayableErrorMessage = (error: unknown): string | undefined => {
  if (!(error instanceof AxiosError) || error.code === AxiosError.ERR_CANCELED) return undefined;

  const { response } = error;
  if (!response) return String(i18n.t('qcapp_warning_api_unavailable', { ns: 'axios' }));

  const message: unknown = (response.data as Partial<BaseResponse> | undefined)?.message;
  if (typeof message === 'string' && message.trim() !== '') return message;

  return `${getLocalizedHttpErrorMessage(response.status)} (HTTP ${response.status})`;
};
